import {Pipe, PipeTransform} from '@angular/core';

/**
 * A short, glanceable "when": `2 min ago`, `Yesterday`, `Mon`, `Aug 28`.
 *
 * The Messages list row has one narrow slot for the last activity time, and a full date-time there is both
 * too wide and more precision than the reader wants — what matters at a glance is "today / this week / a
 * while ago". The exact timestamp is on each message inside the thread.
 */
@Pipe({name: 'eeRelativeTime', standalone: false})
export class RelativeTimePipe implements PipeTransform {
  private static readonly MINUTE_MS = 60_000;
  private static readonly HOUR_MS = 60 * RelativeTimePipe.MINUTE_MS;
  private static readonly DAY_MS = 24 * RelativeTimePipe.HOUR_MS;

  transform(value: string | number | Date | null | undefined, now: Date = new Date()): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    const time = date.getTime();
    if (Number.isNaN(time)) {
      return '';
    }

    const elapsed = now.getTime() - time;
    // A clock skew between the browser and the server can put a just-sent message a few seconds in the
    // future; "in 4 seconds" would be nonsense, so anything not yet past reads as just now.
    if (elapsed < RelativeTimePipe.MINUTE_MS) {
      return 'Just now';
    }
    if (elapsed < RelativeTimePipe.HOUR_MS) {
      return `${Math.floor(elapsed / RelativeTimePipe.MINUTE_MS)} min ago`;
    }

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (time >= startOfToday) {
      const hours = Math.floor(elapsed / RelativeTimePipe.HOUR_MS);
      return hours === 1 ? '1 hr ago' : `${hours} hrs ago`;
    }
    if (time >= startOfToday - RelativeTimePipe.DAY_MS) {
      return 'Yesterday';
    }
    // Inside the last week a weekday name is the most readable form; beyond that it becomes ambiguous.
    if (time >= startOfToday - 6 * RelativeTimePipe.DAY_MS) {
      return date.toLocaleDateString(undefined, {weekday: 'short'});
    }
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
    }
    return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
  }
}
